import type { LucideIcon } from "lucide-react-native";
import type { ContentBlockId } from "@src/packages/content";

// ─── Shared types for the unified care schedule ─────────────

export type MedForm = "pen" | "vial" | "pill" | "suppository";

export type MedicationScheduleItem = {
  type: "medication";
  id: string;
  name: string;
  dosage: string;
  form: MedForm;
  route: string;
  time: string;
  timing: "morning" | "evening" | "as-needed";
  color: string;
  /** "daily" = repeats every day, "once" = single occurrence */
  recurrence: "daily" | "once";
  /** e.g. "Day 1" */
  sinceDay?: string;
  /** e.g. "Day 14" — when the recurring med ends */
  untilDay?: string;
  recentlyChanged?: boolean;
  /** Content registry lookup for the factsheet */
  brandName?: string;
  /** Extra context passed to the factsheet sheet */
  userContext?: {
    dosage?: string;
    route?: string;
    selectedForm?: string;
    timeTaken?: string[];
    startDate?: string;
    endDate?: string | null;
    notes?: string;
    sinceDay?: string;
    history?: {
      cycle: string;
      dosage: string;
      outcome?: string;
      note?: string;
    }[];
    prescription?: {
      prescriber: string;
      pharmacy?: string;
      rxNumber?: string;
      refillsRemaining?: number;
      prescribedDate?: string;
      quantity?: string;
    };
  };
};

export type AppointmentScheduleItem = {
  type: "appointment";
  id: string;
  /** Raw API appointment UUID — use this for delete/update calls */
  appointmentId?: string;
  title: string;
  subtitle: string;
  time?: string;
  /** Raw persisted end fields used to display and restore duration on edit. */
  endDate?: string | null;
  endTime?: string | null;
  timeZone?: string | null;
  icon: LucideIcon;
  isUrgent?: boolean;
  factsheetId?: ContentBlockId;
  consultationType?: string[] | null;
  location?: string | null;
  isOnline?: boolean | null;
  meetingLink?: string | null;
  notes?: string | null;
  /** Ownership comes from the appointments API and controls mutation access. */
  ownership?: "patient" | "provider";
  ownerId?: string;
  providerId?: string | null;
  source?: "provider" | "patient" | "partner" | "system" | null;
};

export type AppointmentDetailItem = AppointmentScheduleItem & { date: string };

export type ScheduleEntry =
  | (MedicationScheduleItem & { date: string })
  | (AppointmentScheduleItem & { date: string });

/**
 * Derive the recurrence pill ("Daily" / "Once") for a medication from its
 * start / end dates. The patient-medication schema has no explicit
 * frequency field, so a course is implicitly daily across its window. A
 * single-day window (start === end) means a one-shot dose — e.g. trigger
 * shot — which reads as "Once".
 */
export function deriveRecurrence(args: {
  startDate?: string | null;
  endDate?: string | null;
}): "daily" | "once" {
  const { startDate, endDate } = args;
  if (startDate && endDate && startDate === endDate) return "once";
  return "daily";
}
